import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as signalR from '@microsoft/signalr';
import { fetchDashboardInsights, fetchJobInsights } from '../api/insightsApi';

const HUB_URL = `${import.meta.env.VITE_API_URL}/hub/liverefresh`;

// Builds a hub connection that invalidates the matching insights query and
// surfaces an isRegenerating flag for "refreshing insights…" UI. Each caller
// gets its own connection — matches the per-hook pattern in useJob/useCustomer.
function useInsightsSignalR(scope, queryKey, jobIdMatcher) {
    const queryClient = useQueryClient();
    const [isRegenerating, setIsRegenerating] = useState(false);

    useEffect(() => {
        const connection = new signalR.HubConnectionBuilder()
            .withUrl(HUB_URL, {
                accessTokenFactory: () =>
                    window.Clerk.session.getToken({ template: 'marginpal-api' }),
            })
            .withAutomaticReconnect()
            .build();

        connection.on('UpdateInsights', (incomingJobId) => {
            if (jobIdMatcher(incomingJobId)) {
                queryClient.invalidateQueries({ queryKey });
            }
        });

        connection.on('InsightsRegenerating', (incomingJobId, value) => {
            if (jobIdMatcher(incomingJobId)) {
                setIsRegenerating(!!value);
            }
        });

        connection.start().catch(err => console.error('Insights SignalR error:', err));

        return () => { connection.stop(); };
        // jobIdMatcher is captured by closure; queryKey is stable per scope.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [queryClient, scope]);

    return { isRegenerating, setIsRegenerating };
}

export function useDashboardInsights() {
    const query = useQuery({
        queryKey: ['dashboardInsights'],
        queryFn: fetchDashboardInsights,
        staleTime:       10 * 60 * 1000,  // 10 minutes
        placeholderData: prev => prev,
    });

    const { isRegenerating, setIsRegenerating } = useInsightsSignalR(
        'dashboard',
        ['dashboardInsights'],
        (incomingJobId) => incomingJobId == null,
    );

    // Cold-load state: when the API returns isRegenerating=true the hub may
    // not have connected yet, so seed from the response. SignalR overrides
    // afterwards via the boundary events.
    useEffect(() => {
        if (query.data?.isRegenerating != null) setIsRegenerating(query.data.isRegenerating);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.data?.isRegenerating]);

    return { ...query, isRegenerating };
}

export function useJobInsights(jobId) {
    const query = useQuery({
        queryKey: ['jobInsights', jobId],
        queryFn: () => fetchJobInsights(jobId),
        enabled: !!jobId,
        staleTime:       10 * 60 * 1000,  // 10 minutes
        placeholderData: prev => prev,
    });

    const numericId = jobId != null ? Number(jobId) : null;
    const { isRegenerating, setIsRegenerating } = useInsightsSignalR(
        `job-${numericId}`,
        ['jobInsights', jobId],
        (incomingJobId) => incomingJobId != null && incomingJobId === numericId,
    );

    useEffect(() => {
        if (query.data?.isRegenerating != null) setIsRegenerating(query.data.isRegenerating);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query.data?.isRegenerating]);

    return { ...query, isRegenerating };
}
